/**
 * Created by piotr.pozniak@thebeaverhead.com on 21/08/2018.
 *
 * Renders the AppShell/PageHeader, translating the legacy `title`/
 * `headerControls`/`className` props (see mapLegacyHeaderProps) that every
 * call site still passes.
 *
 * @param {string|Element} title
 * @param {string=} subtitle - one-line PageHeader sub-line, plain string titles only
 * @param {string=""} className
 * @param {string} icon
 * @param {object} iconImg
 * @param {string} sidebarId
 * @param {array} headerControls
 * @param {ReactNode=} header - full-bleed replacement for the shell's own
 *   PageHeader, rendered in the same unpadded slot (compact layout only) —
 *   e.g. BuilderIdentityBand on CalendarPage/AlertPage, which needs to span
 *   the full column width, not sit inset like PageHeader/title do.
 * @param {array} children
 *
 * @returns {Element}
 * @constructor
 */
import React, { useEffect } from "react";
import classNames from "classnames";
import { updateJquery } from "../../tools";
import appConfig from "../../../../appConfig";
import AppShell from "../AppShell/AppShell/AppShell";
import PageHeader from "../AppShell/PageHeader/PageHeader";
import { useSidebarNavData } from "./useSidebarNavData";
import { useSidebarAlerts } from "./useSidebarAlerts";
import { useAccountShellData } from "./useAccountShellData";
import { mapLegacyHeaderProps } from "./mapLegacyHeaderProps";
import styles from "./index.module.scss";

const Index = ({ title, subtitle, className = "", sidebarId, headerControls, header, children }) => {
  useEffect(() => {
    updateJquery();

    return () => {
      updateJquery();
    };
  }, []);

  const { nav } = useSidebarNavData();
  const { alerts, dismissAlert } = useSidebarAlerts();
  const {
    workspaces,
    currentWorkspace,
    onSwitchWorkspace,
    account,
    impersonating,
    impersonation,
    renderAccountMenu,
  } = useAccountShellData();

  const {
    title: pageTitle,
    actions,
    extraHeaderBlock,
    compact,
  } = mapLegacyHeaderProps(title, headerControls, className);

  return (
    <AppShell
      sidebar={{
        workspaces,
        currentWorkspace,
        onSwitchWorkspace,
        nav,
        activeKey: sidebarId,
        alerts,
        onDismissAlert: dismissAlert,
        account,
        impersonating,
        renderAccountMenu,
        branding: appConfig.branding,
      }}
      impersonation={impersonation}
    >
      {compact ? (
        <div className={styles.fullHeightWrap}>
          {header || (pageTitle && <PageHeader title={pageTitle} subtitle={subtitle} actions={actions} compact />)}
          <div className={classNames(styles.fullHeightBody, { [styles["fullHeightBody--flush"]]: header })}>
            {extraHeaderBlock}
            {children}
          </div>
        </div>
      ) : !pageTitle ? (
        children
      ) : (
        <>
          <PageHeader title={pageTitle} subtitle={subtitle} actions={actions} />
          <div className={styles.body}>
            {extraHeaderBlock}
            {children}
          </div>
        </>
      )}
    </AppShell>
  );
};

export default Index;
